import { makeAutoObservable } from 'mobx'
import { type IAvatarInfo } from 'shared/ui'
import type {
  IResponseCampaignStatus,
  IResponseFullCampaign,
  IResponseShortCampaign,
} from 'entities/Campaign/api/types'
import { CampaignStatistics } from 'entities/Campaign/model/CampaignStatistics'
import { CampaignApi } from 'entities/Campaign/api/campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { getCampaignActions } from 'pages/campaigns/actions'

export class Campaign {
  id: number
  name: string
  status: IResponseCampaignStatus
  statistics: CampaignStatistics | null
  steps: IResponseFullCampaign['steps'] | null = null
  user: IResponseShortCampaign['user'] | null = null
  settings: IResponseFullCampaign['settings'] | null = null
  private _response: IResponseFullCampaign | IResponseShortCampaign

  constructor(
    item: IResponseFullCampaign | IResponseShortCampaign,
    private onSuccessDelete: () => void,
    private _entities: IEntitiesStore
  ) {
    this._response = item
    this.id = item.id
    this.name = item.name
    this.status = item.status
    if ('steps' in item) {
      this.steps = item.steps
    }
    if ('user' in item) {
      this.user = item.user
    }
    this.statistics = item.analytics ? new CampaignStatistics(item.analytics) : null
    if ('settings' in item) {
      this.settings = item.settings || null
    }
    makeAutoObservable(this)
  }

  syncOrigin = (item: IResponseFullCampaign) => {
    this._response = item
    this.id = item.id
    this.name = item.name
    this.status = item.status
    this.steps = item.steps
    this.statistics = item.analytics ? new CampaignStatistics(item.analytics) : null
  }

  get webhook_url() {
    return this._response.webhook_url
  }

  get actions() {
    return getCampaignActions(this, this.onSuccessDelete, this._entities)
  }

  get isDraft() {
    return this.status === 'draft'
  }

  get userAvatarInfo(): IAvatarInfo | null {
    if (this.user) {
      return {
        firstName: this.user.first_name,
        lastName: this.user.last_name,
        number: this.user.number,
        image: this.user.photo_url,
      }
    }
    return null
  }

  get userDisplayInfo() {
    if (this.user) {
      const name = `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim()
      return name || this.user?.number
    }
    return ''
  }

  get hasStatistics() {
    return Number.isFinite(this.statistics?.in_progress)
  }

  refresh = async () => {
    try {
      const { data } = await CampaignApi.getById(this.id)
      this.syncOrigin(data.data)
    } catch (e) {
      console.error(e)
    }
  }
}
